import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import axios from 'axios';

/**
 * The number of open jobs, for the home page: a figure that counts up to the real count when the page opens.
 *
 * It is the count Browse shows with no filter (`GET /api/public/stats/jobs`, read at most every ten minutes on the server), so the
 * page and the Jobs screen agree. A marketing number must never be a wrong or an empty one: nothing is shown until a real, positive
 * count has arrived, and a failed call leaves the page as it was. The animation is skipped for anyone who has asked their system for
 * less motion, and a screen reader is given the final figure, not the numbers on the way.
 */

const DURATION = 1400;

const reducedMotion = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** The figure to show for a moment in the count-up: eases out, ends exactly on the target. */
export const stepValue = (target, elapsed, duration = DURATION) => {
  if (elapsed >= duration) return target;
  const progress = Math.max(0, elapsed) / duration;
  return Math.floor(target * (1 - (1 - progress) ** 3));
};

const OpenJobsCount = () => {
  const [target, setTarget] = useState(null);
  const [shown, setShown] = useState(0);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const response = await axios.get('/api/public/stats/jobs');
        const count = response?.data?.openJobs;
        if (live && Number.isInteger(count) && count > 0) setTarget(count);
      } catch {
        // Left out rather than guessed: the page reads the same without it.
      }
    })();
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (target === null) return undefined;
    if (reducedMotion() || typeof window.requestAnimationFrame !== 'function') {
      setShown(target);
      return undefined;
    }
    let frame;
    let started;
    const tick = (now) => {
      if (started === undefined) started = now;
      setShown(stepValue(target, now - started));
      if (now - started < DURATION) frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [target]);

  if (target === null) return null;

  return (
    <Box data-testid="open-jobs-count" sx={{ textAlign: 'center', mb: 3 }} role="status">
      <Typography
        component="p"
        aria-hidden
        sx={{ fontSize: { xs: 40, md: 52 }, fontWeight: 800, lineHeight: 1.1, color: 'primary.main', fontVariantNumeric: 'tabular-nums' }}
      >
        {shown.toLocaleString('en-US')}
      </Typography>
      <Typography variant="subtitle1" component="p" sx={{ fontWeight: 600 }}>
        <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>{target.toLocaleString('en-US')} </span>
        open jobs, and counting
      </Typography>
    </Box>
  );
};

export default OpenJobsCount;
